import { itemType, toolLevel } from '../enums.js'
import {
  RESOURCE_SPAWN_CHANCE,
  ORE_SPAWN_CHANCE,
  COAL_SPAWN_CHANCE,
  STONE_SPAWN_CHANCE,
} from '../constants.js'
import { randomChoice } from '../utils/randomChoice.js'
import { randomNumberService } from '../common/services/randomNumber.js'
// eslint-disable-next-line no-unused-vars
import { Factory } from '../interfaces/Factory.js'

import OreFactory from './OreFactory.js'
import CoalFactory from './CoalFactory.js'
import StoneFactory from './StoneFactory.js'

/**
 * Object for private cache of factory instances
 */
const factoryInstances: Record<string, Factory | null> = {}

/**
 * Var for caching reference to instance of ResourceFactory
 */
let instance: ResourceFactory | null = null

/**
 * Used for spawning mined resources
 * @constructor
 */
export default class ResourceFactory {
  resourceOptions: { weight: number; itemType: itemType }[]

  constructor() {
    this.resourceOptions = [
      { weight: ORE_SPAWN_CHANCE, itemType: itemType.ORE as itemType },
      {
        weight: COAL_SPAWN_CHANCE,
        itemType: itemType.FUEL as itemType,
      },
      {
        weight: STONE_SPAWN_CHANCE,
        itemType: itemType.STONE as itemType,
      },
    ]
  }

  /**
   * Retrieve a reusable instance of ResourceFactory
   * @static
   */
  static instance(): ResourceFactory {
    if (!instance) {
      instance = new ResourceFactory()
    }

    return instance
  }

  /**
   * Generate an instance for specific factory
   * @returns A factory if one exists for type, default return is null
   */
  static generateFactoryInstance(type: itemType): Factory | null {
    switch (type) {
      case itemType.STONE:
        return new StoneFactory()

      case itemType.FUEL:
        return new CoalFactory()

      case itemType.ORE:
        return new OreFactory()

      default:
        return null
    }
  }

  /**
   * Retrieve a specific factory for generating resources. Will create and cache
   * a factory instance for reuse.
   */
  static getFactoryForItemType = (type: itemType) => {
    if (!factoryInstances[type]) {
      factoryInstances[type] = ResourceFactory.generateFactoryInstance(type)
    }

    return factoryInstances[type]
  }

  /**
   * Use dice roll and resource factories to generate resources at random
   * @returns array of resource objects
   */
  generateResources(shovelLevel: toolLevel): Array<farmhand.item> {
    let resources: farmhand.item[] = []

    let spawnChance = RESOURCE_SPAWN_CHANCE

    switch (shovelLevel) {
      case toolLevel.BRONZE:
        spawnChance += 0.1
        break

      case toolLevel.IRON:
        spawnChance += 0.2
        break

      case toolLevel.SILVER:
        spawnChance += 0.3
        break

      case toolLevel.GOLD:
        spawnChance += 0.5
        break

      default:
    }

    if (randomNumberService.isRandomNumberLessThan(spawnChance)) {
      const opt = randomChoice(this.resourceOptions)
      const factory = ResourceFactory.getFactoryForItemType(opt.itemType)

      if (factory) {
        const generated = factory.generate()

        if (generated) {
          resources = Array.isArray(generated) ? generated : [generated]
        }
      }
    }

    return resources
  }
}
